import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { useState } from 'react';
import FormTabs from '@/features/agent/form/components/tabs/FormTabs';
import type { FormTabType } from '@/features/agent/form/constants';
import useFetchCandidateForm from '@/features/agent/form/api/useFetchCandidateForm';
import FormSetupTutorial from '@/features/agent/form/components/tutorials/FormSetupTutorial';
import FormCompletionScreen from '@/features/agent/form/components/FormCompletionScreen';
import FormHomeTab from '@/features/agent/form/components/tabs/home/FormHomeTab';
import Tab from '@/features/agent/form/components/tabs/question/Tab';
import FormThanksTab from '@/features/agent/form/components/tabs/thanks/FormThanksTab';
import FormReviewTab from '@/features/agent/form/components/tabs/review/FormReviewTab';
import Loading from '@/components/Loading';

const formSearchSchema = z.object({
  tab: z.enum(['home', 'question', 'thanks', 'review']).optional().default('home'),
  answerId: z.coerce.number().optional(),
});

export const Route = createFileRoute('/agent/form/')({
  component: FormPage,
  staticData: { title: '登録フォーム' },
  validateSearch: formSearchSchema,
});

function FormPage() {
  const { tab, answerId } = Route.useSearch();
  const navigate = useNavigate({ from: '/agent/form' });
  const { data: candidateForm, isLoading, error, refetch } = useFetchCandidateForm();
  const [isCompleted, setIsCompleted] = useState(false);
  const [createdFormUrl] = useState<string | undefined>(undefined);

  const activeTab: FormTabType = tab ?? 'home';

  const handleTabChange = (newTab: FormTabType) => {
    navigate({
      to: '/agent/form',
      search: { tab: newTab },
      replace: true,
    });
  };

  const handleFormCreated = async () => {
    // フォーム作成後、データを再取得
    await refetch();
    setIsCompleted(true);
  };

  const handleGoHomeFromCompletion = () => {
    setIsCompleted(false);
    navigate({
      to: '/agent/form',
      search: { tab: 'home' },
    });
  };

  // 完了画面を表示
  if (isCompleted) {
    return <FormCompletionScreen formUrl={createdFormUrl} onGoHome={handleGoHomeFromCompletion} />;
  }

  // ローディング中
  if (isLoading) {
    return (
      <div className="flex min-h-[calc(100vh-120px)] items-center justify-center">
        <Loading />
      </div>
    );
  }

  // フォームが存在しない場合（404）はチュートリアル画面を表示
  if (!candidateForm) {
    return <FormSetupTutorial onFormCreated={handleFormCreated} />;
  }

  // エラーが発生した場合（404以外のエラー）
  if (error) {
    return (
      <div className="flex min-h-[calc(100vh-120px)] items-center justify-center">
        <div className="text-center">
          <p className="text-lg font-bold text-main">エラーが発生しました</p>
          <p className="mt-2 text-sm text-gray">
            フォームの読み込みに失敗しました。ページをリロードしてください。
          </p>
        </div>
      </div>
    );
  }

  // フォームが存在する場合は既存のタブUIを表示
  const renderTabContent = () => {
    switch (activeTab) {
      case 'home':
        return <FormHomeTab candidateForm={candidateForm} />;
      case 'question':
        return <Tab candidateForm={candidateForm} />;
      case 'thanks':
        return <FormThanksTab candidateForm={candidateForm} />;
      case 'review':
        return (
          <FormReviewTab
            candidateForm={candidateForm}
            initialAnswerId={answerId}
            onCloseDetailModal={() =>
              navigate({ to: '/agent/form', search: { tab: 'review' }, replace: true })
            }
          />
        );
      default:
        return <FormHomeTab candidateForm={candidateForm} />;
    }
  };

  return (
    <div className="p-3">
      <FormTabs currentTab={activeTab} onTabChange={handleTabChange} />
      {renderTabContent()}
    </div>
  );
}
